import React, { useState } from "react";
import { Modal, Form, Input, Button, Switch, Upload, message } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import FormModal from "../../components/FormModal";
import DialogFooter from "../../components/DialogFooter";
import { extractMsg } from "../../utils/errorUtils";

const { Dragger } = Upload;

export function CreateDirectoryDialog({ open, onClose, onSave }) {
  const [form] = Form.useForm();
  return (
    <FormModal
      title="Create Directory"
      open={open}
      onClose={() => {
        form.resetFields();
        onClose();
      }}
      onFinish={async (v) => {
        try {
          await onSave(v);
          form.resetFields();
          onClose();
        } catch (error) {
          message.error(extractMsg(error, "Failed to create directory"));
        }
      }}
      form={form}
      initialValues={{ recursive: true }}
    >
      <Form.Item
        name="directory"
        label="Directory Name"
        rules={[{ required: true }]}
      >
        <Input placeholder="Enter directory name" />
      </Form.Item>
      <Form.Item name="path" label="Path" rules={[{ required: true }]}>
        <Input placeholder="Enter path" />
      </Form.Item>
      <Form.Item name="recursive" label="Recursive" valuePropName="checked">
        <Switch />
      </Form.Item>
    </FormModal>
  );
}

export function DeleteDirectoryDialog({ open, onClose, onSave }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const handleFinish = async (values) => {
    setLoading(true);
    try {
      await onSave(values);
      form.resetFields();
      onClose();
    } catch (error) {
      message.error(extractMsg(error, "Failed to delete directory"));
      console.error("Failed to delete directory", error);
    } finally {
      setLoading(false);
    }
  };
  return (
    <Modal
      title="Delete Directory"
      open={open}
      onCancel={() => {
        form.resetFields();
        onClose();
      }}
      footer={null}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{ force: false }}
      >
        <Form.Item name="path" label="Path" rules={[{ required: true }]}>
          <Input placeholder="Enter path to delete" />
        </Form.Item>
        <Form.Item
          name="force"
          label="Recursive (Force)"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        <DialogFooter
          onCancel={() => {
            form.resetFields();
            onClose();
          }}
          onSave={form.submit}
          loading={loading}
          saveText="Delete"
          danger
        />
      </Form>
    </Modal>
  );
}

export function UploadFileDialog({ open, onClose, onSave }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const handleFinish = async (values) => {
    if (!fileList.length) {
      message.error("Please select a file to upload");
      return;
    }
    setLoading(true);
    try {
      await onSave({
        filename: values.filename,
        hdfsPath: values.hdfsPath,
        file: fileList[0],
      });
      form.resetFields();
      setFileList([]);
      onClose();
    } catch (error) {
      message.error(extractMsg(error, "Failed to upload file"));
      console.error("Failed to upload file", error);
    } finally {
      setLoading(false);
    }
  };

  const uploadProps = {
    onRemove: () => setFileList([]),
    beforeUpload: (file) => {
      const isValid = file.size <= 256 * 1024 * 1024;
      if (!isValid) {
        message.error("File size exceeds the 256 MB limit");
        return Upload.LIST_IGNORE;
      }
      setFileList([file]);
      return false;
    },
    fileList,
    maxCount: 1,
  };

  return (
    <Modal
      title="Upload File"
      open={open}
      onCancel={() => {
        form.resetFields();
        setFileList([]);
        onClose();
      }}
      footer={null}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Form.Item
          name="filename"
          label="Filename"
          rules={[{ required: true }]}
        >
          <Input placeholder="Enter filename" />
        </Form.Item>
        <Form.Item
          name="hdfsPath"
          label="HDFS Path"
          rules={[{ required: true }]}
        >
          <Input placeholder="Enter HDFS path" />
        </Form.Item>
        <Form.Item label="File">
          <Dragger {...uploadProps}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Click or drag file to this area to upload (max 256 MB).
            </p>
          </Dragger>
        </Form.Item>
        <DialogFooter
          onCancel={() => {
            form.resetFields();
            setFileList([]);
            onClose();
          }}
          onSave={form.submit}
          loading={loading}
          saveText="Upload"
        />
      </Form>
    </Modal>
  );
}
